import React, { useState, useEffect } from 'react';

import useBatchOperation from '@hooks/shared/useBatchOperation';
import { Modal, Input, Form, Space, Button, message, Select } from 'antd';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

interface OrderBatchService {
  batchSplitFulfillments?: (ids: string[], params: Record<string, unknown>) => Promise<unknown>;
  [key: string]: unknown;
}

interface PanelContentProps {
  showPanel?: boolean;
  onClosePanel: () => void;
  selected: Set<string>;
  query: Record<string, unknown>;
  setLastModified: (v: number) => void;
  service?: OrderBatchService;
  addJob?: (jobId: string, jobName: string) => void;
  // 由 caller (OrderList) 傳入但本元件未使用，保留以維持原 runtime 行為
  role?: string;
}

type TargetProductType = 'out_of_stocks' | 'product_Skus' | null;

const PanelContent: React.FC<PanelContentProps> = ({ showPanel = false, onClosePanel, selected, query, setLastModified, service, addJob }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [targetProductType, setTargetProductType] = useState<TargetProductType>(null)
  const [productSkus, setProductSkus] = useState('')

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service , addJob)

  const onCancel = () => {
    onClosePanel();
    form.resetFields();
    setTargetProductType(null)
    setProductSkus('')
  };

  useEffect(() => {
    if (showPanel) {
      const defaultProductSkus = query['products.Sku'] || '';
      if (typeof defaultProductSkus === 'string') {
        setProductSkus(defaultProductSkus)
      }
      else if (Array.isArray(defaultProductSkus)) {
        setProductSkus(defaultProductSkus.join('\n'))
      }
    }
  }, [showPanel])

  const onSubmit = () => {
    // 沒選目標商品類型 → 後端必拒，直接擋下避免 silent fail
    if (!targetProductType) {
      message.error(t('admin.orders.list.batch_panel.target_product_type_placeholder') as string)
      return
    }

    const skus = productSkus.split("\n").map(s => s.trim()).filter(Boolean)
    if (targetProductType === 'product_Skus' && skus.length === 0) {
      message.error(t('admin.orders.list.batch_panel.product_Skus_placeholder') as string)
      return
    }

    const params = {
      target_product_type: targetProductType,
      product_Skus: skus
    }

    const url = 'orders/batch_split_fulfillments.json'
    const confirmKey = 'admin.orders.list.batch_panel.confirm_split'
    const jobName = t('admin.orders.list.batch_panel.split_fulfillments') as string
    const serviceAction = service
      ? () => service.batchSplitFulfillments!(Array.from(selected), params)
      : null

    Modal.confirm({
      title: t(confirmKey, { count: selected.size }) as string,
      onOk: () => {
        executeBatchOperation({
          task: serviceAction,
          url,
          selected,
          params,
          jobName,
          onSuccess: () => setLastModified(Date.now())
        })
        onCancel()
      }
    });
  }

  return (
    <Modal
      title={t('admin.orders.list.batch_panel.title')}
      open={showPanel}
      onCancel={onCancel}
      width={700}
      footer={
        <Space>
          <Button onClick={onCancel}>
            {t('admin.orders.list.batch_panel.cancel_button')}
          </Button>
          <Button type="primary" onClick={onSubmit}>
            {t('admin.orders.list.batch_panel.confirm_button')}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 18 }}
      >
        <Form.Item label={t('admin.orders.list.batch_panel.target_product_type_label', { defaultValue: t('admin.orders.list.batch_panel.target_product_type_placeholder') })}>
          <Select
            value={targetProductType ?? undefined}
            options={[
              { value: 'out_of_stocks', label: t('admin.orders.list.batch_panel.target_product_type.out_of_stocks') },
              { value: 'product_Skus', label: t('admin.orders.list.batch_panel.target_product_type.product_Skus') }
            ]}
            onChange={(v) => setTargetProductType((v ?? null) as TargetProductType)}
            placeholder={t('admin.orders.list.batch_panel.target_product_type_placeholder') as string}
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        {targetProductType === 'product_Skus' && (
          <Form.Item label=" " colon={false}>
            <TextArea
              value={productSkus}
              onChange={(e) => setProductSkus(e.target.value)}
              placeholder={t('admin.orders.list.batch_panel.product_Skus_placeholder') as string}
              rows={6}
              allowClear
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

export default PanelContent;
